import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import clsx from 'clsx';

/**
 * Bow Tuning Cheat Sheets — the canonical reference tables used across the
 * whole tuning wizard.
 *
 * Two reference views:
 *  1. ADJUSTMENT RESULTS MATRIX — effect of every action (twist/untwist cables,
 *     twist/untwist string, move draw stops in/out) on every measurable
 *     property (cam rotation, draw length, draw weight, valley/let-off, ATA,
 *     brace height). This is the source of truth for every step that says
 *     "twist the string" or "move the draw stop."
 *  2. POINT OF IMPACT TUNING GUIDE — bareshaft / broadhead group position
 *     relative to fletched group → list of corrective adjustments.
 *
 * Every "fix it" tip in any step should reference these tables. If a
 * step contradicts the matrix, the matrix wins.
 *
 * Verified sources: Hamskea Archery, Vapor Trail, Easton tune charts,
 * Lancaster Archery Supply, GoHunt tuning guide, Yates Archery tuning reference.
 */

interface CheatSheetButtonProps {
  /** Which sheet to default-open. Defaults to 'adjustment-matrix'. */
  defaultSheet?: SheetKey;
  /** Label for the button (defaults to "Cheat sheet"). */
  label?: string;
}

/** Slide-in cheat-sheet panel toggle. Drop this anywhere in a step. */
export function CheatSheetButton({ defaultSheet = 'adjustment-matrix', label = 'Cheat sheet' }: CheatSheetButtonProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<SheetKey>(defaultSheet);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<BookOpen size={14} />}
        onClick={() => { setActive(defaultSheet); setOpen(true); }}
      >
        {label}
      </Button>
      {open && <CheatSheetPanel active={active} setActive={setActive} onClose={() => setOpen(false)} />}
    </>
  );
}

type SheetKey = 'adjustment-matrix' | 'poi-tuning' | 'paper-tear';

function CheatSheetPanel({
  active,
  setActive,
  onClose,
}: {
  active: SheetKey;
  setActive: (k: SheetKey) => void;
  onClose: () => void;
}) {
  // Lock body scroll while the sheet is open so the underlying step doesn't
  // scroll behind it on iOS. Restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ──────────────────────────────────────────────────────────────────────
  // PORTAL into <body> so the panel ALWAYS escapes any transformed/
  // overflow-hidden ancestor in the wizard. Without this, mobile users
  // saw the sheet trapped at the bottom of the wizard sidebar scroll area
  // — `position: fixed` is broken by any ancestor with `transform`,
  // `filter`, `perspective`, or `will-change`.
  //
  // Width: 100vw on mobile (full screen), 640px slide-in on >= sm.
  // Height: 100dvh (dynamic viewport) so mobile Safari address bar collapse
  // doesn't leave the bottom of the panel cut off.
  // ──────────────────────────────────────────────────────────────────────
  const panel = (
    <div
      className="fixed inset-0 z-[9999] flex"
      role="dialog"
      aria-modal="true"
      style={{ height: '100dvh' }}
    >
      {/* Backdrop — visible on tablet+ where the panel is partial-width.
          Tapping outside closes. */}
      <button
        className="hidden sm:block flex-1 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close cheat sheet"
      />
      <aside
        className="w-full sm:w-[640px] sm:ml-auto bg-zinc-950 sm:border-l border-zinc-800 overflow-y-auto shadow-2xl flex flex-col"
        style={{ height: '100dvh', WebkitOverflowScrolling: 'touch' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-ember-500" />
            <h2 className="font-bold text-zinc-100">Tuning cheat sheets</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 -m-2 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 touch-manipulation"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        {/* Tab switcher */}
        <nav className="flex border-b border-zinc-800 bg-zinc-950 sticky top-[57px] z-10">
          <SheetTab label="Adjustment matrix" sheet="adjustment-matrix" active={active} onClick={setActive} />
          <SheetTab label="Point of impact" sheet="poi-tuning" active={active} onClick={setActive} />
          <SheetTab label="Paper tear" sheet="paper-tear" active={active} onClick={setActive} />
        </nav>

        <div className="p-4 sm:p-5 flex-1">
          {active === 'adjustment-matrix' && <AdjustmentMatrix />}
          {active === 'poi-tuning' && <PointOfImpactGuide />}
          {active === 'paper-tear' && <PaperTearGuide />}
        </div>

        <footer className="px-4 py-3 border-t border-zinc-800 text-[10px] text-zinc-500 leading-snug">
          Sources: Easton tune charts; Hamskea, Vapor Trail rest manuals; Lancaster Archery Supply tuning references;
          Yates Archery tuning guide (2024). Make vertical adjustments BEFORE horizontal.
        </footer>
      </aside>
    </div>
  );

  // Render into <body> — guaranteed to bypass any transformed/clipped ancestor.
  // SSR-safe: skip if document is undefined.
  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
}

function SheetTab({
  label, sheet, active, onClick,
}: {
  label: string; sheet: SheetKey; active: SheetKey; onClick: (s: SheetKey) => void;
}) {
  const isActive = active === sheet;
  return (
    <button
      onClick={() => onClick(sheet)}
      className={clsx(
        'flex-1 px-3 py-2.5 text-xs font-medium border-b-2 transition',
        isActive
          ? 'border-ember-500 text-ember-500'
          : 'border-transparent text-zinc-400 hover:text-zinc-100',
      )}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 1. Adjustment Results Matrix
// ─────────────────────────────────────────────────────────────────────────

/**
 * Row = action you can take on the bow.
 * Cell = effect on the column property. ↑ = increase, ↓ = decrease,
 *   ↑↑/↓↓ = strong effect, ↑/↓ alone = slight effect, "—" = no effect,
 *   "Advance" / "Delay" = cam rotation direction.
 *
 * Source: standardized industry table (Hamskea, Vapor Trail tune sheets,
 * Easton tuning manual). Reproduced from Sparrow Expeditions' verified
 * tuning matrix May 2026.
 */
const ADJUSTMENT_ROWS: Array<{
  action: string;
  cam: string;
  dl: string;
  dw: string;
  valley: string;
  ata: string;
  brace: string;
}> = [
  { action: 'Twist cables',     cam: 'Advance', dl: '↑',   dw: '↑',   valley: '↑ slight', ata: '↓',         brace: '↑ slight' },
  { action: 'Untwist cables',   cam: 'Delay',   dl: '↓',   dw: '↓',   valley: '↓ slight', ata: '↑',         brace: '↓ slight' },
  { action: 'Twist string',     cam: 'Delay',   dl: '↓',   dw: '↓',   valley: '↓ slight', ata: '↓ slight', brace: '↑ slight' },
  { action: 'Untwist string',   cam: 'Advance', dl: '↑',   dw: '↑',   valley: '↑ slight', ata: '↑ slight', brace: '↓ slight' },
  { action: 'Draw stops IN',    cam: 'Slight delay',     dl: '↓ slight', dw: '—', valley: 'Shorten valley, ↓ let-off', ata: '—', brace: '—' },
  { action: 'Draw stops OUT',   cam: 'Slight advance',   dl: '↑ slight', dw: '—', valley: 'Lengthen valley, ↑ let-off', ata: '—', brace: '—' },
];

function AdjustmentMatrix() {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-bold text-base mb-1 text-zinc-100">Adjustment results matrix</h3>
        <p className="text-[12px] text-zinc-400 leading-snug">
          What each cable/string/draw-stop adjustment does to every measurement on your bow.
          This is the canonical reference — every "fix it" tip in the wizard works off this table.
        </p>
      </div>

      <div className="overflow-x-auto border border-zinc-800 rounded-lg">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-zinc-900/60 border-b border-zinc-800">
              <th className="text-left px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">
                Adjustment
              </th>
              <Th>Cam rotation</Th>
              <Th>Draw length</Th>
              <Th>Draw weight</Th>
              <Th>Valley / let-off</Th>
              <Th>ATA</Th>
              <Th>Brace height</Th>
            </tr>
          </thead>
          <tbody>
            {ADJUSTMENT_ROWS.map((row, i) => (
              <tr
                key={row.action}
                className={i % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/30'}
              >
                <td className="px-3 py-2.5 font-semibold text-zinc-100 whitespace-nowrap">{row.action}</td>
                <Cell value={row.cam} />
                <Cell value={row.dl} />
                <Cell value={row.dw} />
                <Cell value={row.valley} />
                <Cell value={row.ata} />
                <Cell value={row.brace} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ──────────── Strother's peak-weight lesson ────────────
          One of the most-overlooked truths in compound tuning. Posted by Kevin
          Strother (Strother Archery / former Bowtech designer) May 2026 — too
          good not to feature. Most archers add twists to ONLY the cables to
          bump peak weight a few pounds; that front-loads the cam, lengthens
          draw, and slows the bow. The fix is to twist BOTH string + cables in
          equal measure so cam timing and DL stay put. */}
      <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 space-y-2">
        <p className="text-[11px] font-mono uppercase tracking-wider text-amber-400">
          Adding peak weight without slowing the bow
        </p>
        <p className="text-[12px] text-zinc-200 leading-relaxed">
          When you want to bump peak draw weight a few pounds: <strong>twist the STRING
          AND cables equally</strong>. NOT just the cables.
        </p>
        <p className="text-[12px] text-zinc-300 leading-relaxed">
          <strong>Why:</strong> twisting only the cables (the common mistake)
          front-loads the cam and lengthens your draw — the bow gets weight back but
          ends up <em>slower</em> than before, with cam timing off. Twisting string +
          cables in proportion shortens both sides so peak weight rises without DL or
          timing changing. (Kevin Strother, May 2026.)
        </p>
        <p className="text-[11px] text-amber-300/90 leading-snug">
          Practical: start with 2 twists in each cable + 1 twist in the string,
          re-measure peak and DL, repeat until on target. Always re-check cam timing
          and tiller after.
        </p>
      </div>

      <div className="space-y-2 text-[11px] text-zinc-400 leading-relaxed">
        <p>
          <strong className="text-zinc-200">Reading the arrows:</strong>{' '}
          <span className="text-emerald-400">↑</span> = increases,{' '}
          <span className="text-rose-400">↓</span> = decreases, "slight" = noticeable
          but small.
        </p>
        <p>
          <strong className="text-zinc-200">Cam rotation — Advance vs Delay:</strong>{' '}
          "Advance" means the cam rotates further toward (or past) full draw at brace.
          "Delay" means it rotates less (sometimes also called "roll back"). Cam advance
          also moves the cam timing dot/mark beyond its factory position.
        </p>
        <p>
          <strong className="text-zinc-200">Even vs single-leg twists:</strong>{' '}
          Twisting BOTH yoke legs of a cable equally changes cable length (affects DL +
          DW). Twisting just one leg (single-leg twist) shifts cam lean / timing without
          changing total cable length — that's how yoke tuning fixes left/right tears
          without affecting DL.
        </p>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">
      {children}
    </th>
  );
}

function Cell({ value }: { value: string }) {
  const colored = value
    .replace(/↑/g, '<span class="text-emerald-400">↑</span>')
    .replace(/↓/g, '<span class="text-rose-400">↓</span>');
  return (
    <td
      className="px-3 py-2.5 text-zinc-300 whitespace-nowrap"
      dangerouslySetInnerHTML={{ __html: colored }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 2. Point of Impact tuning guide (bareshaft / broadhead vs fletched)
// ─────────────────────────────────────────────────────────────────────────

interface POIDirection {
  direction: 'high' | 'low' | 'left' | 'right';
  label: string;
  solutions: string[];
}

/**
 * Where the bareshaft (or broadhead) lands relative to the fletched group.
 * Solutions are ordered: try in this order, do vertical FIRST, then horizontal.
 *
 * Yoke twist convention (right-handed shooter, looking at the bow from the back
 * with the string toward you): "left yoke" = the buss-cable leg on the left
 * side of the cam (your left). "Right yoke" = right side.
 */
const POI_DIRECTIONS: POIDirection[] = [
  {
    direction: 'high',
    label: 'Bareshaft / broadhead lands HIGH of fletched group',
    solutions: [
      'Move the nocking point UP (raise nock-set or D-loop on the string)',
      'Move the rest DOWN (lower the launcher height)',
      'Advance the TOP cam — add twists to the top cable end',
      'Delay the BOTTOM cam — remove twists from the bottom cable end',
    ],
  },
  {
    direction: 'low',
    label: 'Bareshaft / broadhead lands LOW of fletched group',
    solutions: [
      'Move the nocking point DOWN (lower nock-set or D-loop on the string)',
      'Move the rest UP (raise the launcher height)',
      'Advance the BOTTOM cam — add twists to the bottom cable end',
      'Delay the TOP cam — remove twists from the top cable end',
    ],
  },
  {
    direction: 'left',
    label: 'Bareshaft / broadhead lands LEFT of fletched group (RH shooter)',
    solutions: [
      'Move the cam RIGHT (shim or yoke-twist toward shooter-right)',
      'Twist the RIGHT yoke — untwist the LEFT yoke (single-leg twists)',
      'Move the rest LEFT (toward the riser)',
    ],
  },
  {
    direction: 'right',
    label: 'Bareshaft / broadhead lands RIGHT of fletched group (RH shooter)',
    solutions: [
      'Move the cam LEFT (shim or yoke-twist toward shooter-left)',
      'Twist the LEFT yoke — untwist the RIGHT yoke (single-leg twists)',
      'Move the rest RIGHT (away from the riser)',
    ],
  },
];

function PointOfImpactGuide() {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-bold text-base mb-1 text-zinc-100">Point of impact tuning guide</h3>
        <p className="text-[12px] text-zinc-400 leading-snug">
          Where your bareshaft (or broadhead) groups relative to your fletched arrows tells
          you what's still out of tune. <strong className="text-amber-300">Always do
          vertical adjustments FIRST</strong>, then horizontal.
        </p>
      </div>

      {/* Cross-shaped POI diagram */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-3 text-center">
          Where the bareshaft / broadhead lands
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
          <div />
          <CrossCell direction="high" />
          <div />
          <CrossCell direction="left" />
          <div className="flex items-center justify-center">
            <span className="text-[11px] text-zinc-400 text-center font-semibold">
              Fletched<br/>group<br/>(center)
            </span>
          </div>
          <CrossCell direction="right" />
          <div />
          <CrossCell direction="low" />
          <div />
        </div>
      </div>

      {POI_DIRECTIONS.map((d) => (
        <div key={d.direction} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <p className="text-sm font-semibold text-zinc-100 mb-2">{d.label}</p>
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
            Solutions (try in order)
          </p>
          <ol className="list-decimal list-inside space-y-1 text-[13px] text-zinc-300 leading-relaxed">
            {d.solutions.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      ))}

      <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-3 text-[12px] text-zinc-300 leading-relaxed">
        <p className="font-semibold text-amber-300 mb-1">A note on cam position language</p>
        <p>
          "Advanced" cam position means the cam is NOT rolled all the way into its draw
          stop — it's been moved further along its rotation arc. "Delay" rolls it back
          toward neutral. On most bows you achieve this by twisting (advance) or
          untwisting (delay) the appropriate cable end.
        </p>
      </div>
    </div>
  );
}

function CrossCell({ direction }: { direction: 'high' | 'low' | 'left' | 'right' }) {
  const symbol = {
    high: '●●●',
    low: '●●●',
    left: '●●●',
    right: '●●●',
  }[direction];
  const labelText = {
    high: 'High',
    low: 'Low',
    left: 'Left',
    right: 'Right',
  }[direction];
  return (
    <div className="rounded-md border border-zinc-700 bg-zinc-950 p-2.5 text-center">
      <p className="text-zinc-100 text-base leading-none mb-1">{symbol}</p>
      <p className="text-[10px] text-zinc-400 font-mono uppercase">{labelText}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3. Paper tear quick reference
// ─────────────────────────────────────────────────────────────────────────

interface PaperTear {
  tear: string;
  meaning: string;
  fixes: string[];
}

/**
 * Paper tear pattern → cause + ordered fixes.
 * Convention: "tail-high" means the FLETCHING side of the tear is ABOVE the
 * point hole. Read it as "the back of the arrow is going high relative to the
 * point at the moment of release." Same logic for low/left/right.
 */
const PAPER_TEARS: PaperTear[] = [
  {
    tear: 'Tail-HIGH (back of arrow up)',
    meaning: 'Nock is too high OR rest is too low. Arrow is kicking the rest at release.',
    fixes: [
      'Move nocking point DOWN 1/32" — re-shoot',
      'If still tail-high, raise rest by 1/32" instead',
      'On low-cam bows: tail-high can also mean cam timing — top cam is hitting early',
    ],
  },
  {
    tear: 'Tail-LOW (back of arrow down)',
    meaning: 'Nock is too low OR rest is too high. Less common than tail-high.',
    fixes: [
      'Move nocking point UP 1/32" — re-shoot',
      'If still tail-low, lower rest by 1/32"',
      'Check tiller — positive tiller (top tighter) can cause persistent tail-low tears',
    ],
  },
  {
    tear: 'Tail-LEFT (back of arrow left, RH shooter)',
    meaning: 'Arrow flexing right at release. Could be spine too stiff, cam lean right, or rest too far OUT.',
    fixes: [
      'Move rest LEFT 1/32" — re-shoot',
      'If problem persists: yoke-tune — twist left yoke, untwist right (RH)',
      'If still off: spine may be too stiff; verify with bareshaft tune',
    ],
  },
  {
    tear: 'Tail-RIGHT (back of arrow right, RH shooter)',
    meaning: 'Arrow flexing left at release. Could be spine too weak, cam lean left, or rest too far IN.',
    fixes: [
      'Move rest RIGHT 1/32" — re-shoot',
      'If problem persists: yoke-tune — twist right yoke, untwist left (RH)',
      'If still off: spine may be too weak — common with heavy points + short arrows',
    ],
  },
  {
    tear: 'Diagonal tear (e.g. high-left, low-right)',
    meaning: 'Combination of vertical + horizontal — both need fixing.',
    fixes: [
      'Fix the VERTICAL component first (nock or rest height) — the diagonal usually resolves on its own',
      'Re-shoot. If a residual horizontal tear remains, fix it next',
      'Never chase both at once — you\'ll wash one with the other',
    ],
  },
  {
    tear: 'Random / inconsistent tears',
    meaning: 'Not the bow — usually form (grip torque, peep timing, follow-through) or arrow rest contact.',
    fixes: [
      'Check for vane contact — spray rest + cables with foot powder, shoot 1 arrow, look for streaks',
      'Re-check grip — sloppy/inconsistent grip = sloppy tears',
      'Try a different arrow set — your test arrow may be damaged or weak',
    ],
  },
];

function PaperTearGuide() {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-bold text-base mb-1 text-zinc-100">Paper tear quick reference</h3>
        <p className="text-[12px] text-zinc-400 leading-snug">
          Shoot a fletched arrow through butcher paper at 6 ft. The tear pattern tells you
          what's still off. Always fix vertical tears BEFORE horizontal.
        </p>
      </div>

      {PAPER_TEARS.map((t) => (
        <div key={t.tear} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <p className="text-sm font-semibold text-zinc-100">{t.tear}</p>
          <p className="text-[12px] text-zinc-400 mt-1 mb-2 leading-relaxed">{t.meaning}</p>
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Fix order</p>
          <ol className="list-decimal list-inside space-y-0.5 text-[12px] text-zinc-300 leading-relaxed">
            {t.fixes.map((f, i) => <li key={i}>{f}</li>)}
          </ol>
        </div>
      ))}
    </div>
  );
}
